import React, { useEffect, useState } from 'react'
import styles from './JoinGame.module.css'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { InputField } from '../../../common/components/input/InputField'
import { Button } from '../../../common/components/button/Button'
import { useGameData } from '../../../contexts/PlayerAccountGamesContext'
import { useUser } from '../../../contexts/userContext'
import { ThreeDots } from '../../../common/components/loaders/ThreeDots/ThreeDots'
import { useNotification } from '../../../contexts/NotificationContext'

export const JoinGame: React.FC = () => {
  const { t } = useTranslation()
  const [gamePin, setGamePin] = useState('')
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [isLoading, setisLoading] = useState(false)
  const { joinNewGame, joinNewGameWithName } = useGameData()
  const { user } = useUser()
  const { notifyError, notifySuccess } = useNotification()
  const { pageData } = useGameData()
  const [gameAdded, setGameAdded] = useState(false)

  const getErrorMessage = (msg: string) => {
    switch (msg) {
      case 'The name is already reserved, please select another name':
        return t('player_account_view.errors.name_reserved', 'The name is already reserved, please select another name')
      case 'The login code was incorrect. Please try again.':
        return t('player_account_view.errors.incorrect_code', 'The login code was incorrect. Please try again.')
      default:
        return t(
          'player_account_view.errors.default',
          'An error happened while joining the game. Please try again or contact support.',
        )
    }
  }

  const callJoinNewGame = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    setisLoading(true)
    if (gamePin.trim().length > 5) {
      const response = await joinNewGame(gamePin)
      if (response && response.success) {
        if (response.value.msg === 'OK') {
          setGameAdded(true)
        } else if (response.value.msg === 'PROMPT_NAME') {
          const response2 = await joinNewGameWithName(
            user?.chatName || '',
            response.value.data.new_game_id,
            response.value.data.new_user_id,
          )
          if (response2 && response2.success && response2.value.msg === 'OK') {
            setGameAdded(true)
          } else {
            notifyError({
              title: t('player_account_view.errors.joining_title', 'Failed to join the game'),
              content: getErrorMessage(response2 && response2.success ? response2.value.msg : ''),
            })
          }
        } else {
          notifyError({
            title: t('player_account_view.errors.joining_title', 'Failed to join the game'),
            content: getErrorMessage(response.value.msg),
          })
        }
      } else {
        notifyError({
          title: t('player_account_view.errors.joining_title', 'Failed to join the game'),
          content: getErrorMessage(''),
        })
      }
      setisLoading(false)
    } else if (gamePin.length > 0 && gamePin.length < 6) {
      setisLoading(false)
      setShowErrorMessage(true)
    }
  }

  useEffect(() => {
    const joinedGame = pageData?.find((game) => game.joiningPin === gamePin)
    if (gameAdded && joinedGame) {
      notifySuccess({
        title: t('player_account_view.success.join_title', 'Game joined!'),
        content: t('player_account_view.success.join_content', {
          defaultValue: 'You have joined game %{gamename}',
          gamename: joinedGame?.name,
        }),
      })
      setGameAdded(false)
      setGamePin('')
    }
  }, [gameAdded, gamePin, notifySuccess, pageData, t])

  const handleJoinNewGame = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const joinedGame = pageData?.find((game) => game.joiningPin === gamePin)
    if (!joinedGame) callJoinNewGame(e)
    else {
      notifyError({
        title: t('player_account_view.errors.joining_title', 'Failed to join the game'),
        content: t('player_account_view.errors.joining.already_joined.text', {
          defaultValue: 'You have already joined game %{gamename}',
          gamename: joinedGame.name,
        }),
      })
    }
  }

  const onChangeGamePin = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setGamePin(e.currentTarget.value)
    if (e.currentTarget.value.length === 0) {
      setShowErrorMessage(false)
    }
    if (showErrorMessage && e.currentTarget.value.length > 5) {
      setShowErrorMessage(false)
    }
  }

  return (
    <>
      {isLoading && (
        <>
          <div className={styles.loaderBackgroundOverlay} />
          <div className={styles.loaderContainer}>
            <ThreeDots />
          </div>
        </>
      )}
      <div className={styles.joinGameContainer}>
        <h2 style={{ zIndex: '20' }}>{t('player_account_view.join_game.header', 'Join game')}</h2>
        <form
          className={classNames(styles.addWithPin)}
          onSubmit={handleJoinNewGame}
          onBlur={() => setShowErrorMessage(false)}
        >
          <InputField
            placeholder={t('player_account_view.join_game.placeholder', 'Enter game code')}
            onChange={onChangeGamePin}
            value={gamePin}
            inputContainerClassName={styles.gamePinInput}
            className={classNames(showErrorMessage && styles.overrideDefaultOutline)}
          />
          {showErrorMessage && (
            <p className={styles.warningBadge}>
              {t('player_account_view.join_game.error_message', 'Game code is 6 characters long')}
            </p>
          )}
          <Button type='submit' className={styles.joinGameButton}>
            {t('player_account_view.join_game.button', 'Join')}
          </Button>
        </form>
        <div className={styles.firstCircle} />
        <div className={styles.secondCircle} />
      </div>
    </>
  )
}
